// Sales · Account detail — the licensing hub. Kind-aware: a reseller shows its
// resold downstream customers + margin economics; a direct customer shows its own
// licenses with inline Renew/Revoke. Both show an Entitlements card. Origin-aware
// breadcrumb. Mirrors portal/sales.jsx SalesAccount + Reseller/AccountLicensing.
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { PageHeader, Card, CardHead, Table, Td, Row, Mono, Btn } from '@/components/shared/primitives';
import { Kpi, UtilBar } from '@/components/shared/Kpi';
import { CompanyChip } from '@/components/shared/CompanyChip';
import { MaskedKey } from '@/components/shared/MaskedKey';
import { Pill, TierPill } from '@/components/shared/badges';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ReasonDialog } from '@/components/shared/ReasonDialog';
import { Icon } from '@/components/shared/Icon';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { toast } from '@/components/shared/ToastProvider';
import { apiRequest } from '@/lib/supabase';
import { formatRelativeTime } from '@/lib/utils';
import { money, moneyK } from '@/lib/format';
import { PERSONA, personaBase } from '@/lib/persona';
import { useCanMutate } from '@/context/PersonaProvider';
import { OrgMembersCard } from '@/components/admin/OrgMembersCard';
import { TrialPanel } from '@/components/trial/TrialPanel';
import { getOrganization, getOrgLicenses, getResold, getOrgInstalls, getOrgAllocation, ENTITLEMENTS, type OrgRow, type OrgLicense, type ResoldResponse, type OrgInstall, type TenantAllocation } from '@/lib/api/accounts';

const SALES = PERSONA.sales.accent;

function Econ({ label, value, color }: { label: string; value: string; color?: string }) {
  return <div><div style={{ fontSize: 10.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div><div style={{ fontSize: 16, fontWeight: 700, color: color || 'var(--text-primary)' }}>{value}</div></div>;
}

function EntitlementsCard({ kind }: { kind: 'direct' | 'platform' }) {
  const rows = ENTITLEMENTS[kind];
  return (
    <Card style={{ marginTop: 16 }}>
      <CardHead title="Entitlements" sub={kind === 'platform' ? 'Platform / reseller partner entitlements' : 'Direct customer entitlements'} />
      <div style={{ padding: '14px 20px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '9px 24px' }}>
        {rows.map((e) => (
          <div key={e.k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: e.on ? 'var(--text-primary)' : 'var(--text-muted)' }}>
            <Icon name={e.on ? 'check-circle' : 'minus-circle'} size={15} color={e.on ? 'var(--ps-pill-green-tx)' : 'var(--text-muted)'} />{e.k}
          </div>
        ))}
      </div>
    </Card>
  );
}

export function SalesAccount() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const base = personaBase(location.pathname); // keep the user in their current workspace
  const canMutate = useCanMutate();
  const [org, setOrg] = useState<OrgRow | null>(null);
  const [licenses, setLicenses] = useState<OrgLicense[]>([]);
  const [resold, setResold] = useState<ResoldResponse | null>(null);
  const [installs, setInstalls] = useState<OrgInstall[]>([]);
  const [allocation, setAllocation] = useState<TenantAllocation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The licence the Revoke dialog is about. Its key's last four characters are the typed confirm.
  const revokeTarget = licenses.find((l) => l.id === revokeId) ?? null;

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [o, l, r, ins, alloc] = await Promise.all([
        getOrganization(id),
        getOrgLicenses(id).catch(() => []),
        getResold(id).catch(() => null),
        getOrgInstalls(id).catch(() => []),
        getOrgAllocation(id).catch(() => null), // 404 for non-tenants → null
      ]);
      setOrg(o); setLicenses(l); setResold(r); setInstalls(ins); setAllocation(alloc);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load account');
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `load` is recreated every render; refetch only when the account id changes
  useEffect(() => { load(); }, [id]);

  const renew = async (licId: string) => {
    setBusy(true);
    try {
      const next = new Date();
      next.setFullYear(next.getFullYear() + 1);
      await apiRequest(`/licenses/${licId}/renew`, { method: 'POST', body: JSON.stringify({ expiresAt: next.toISOString() }) });
      toast({ kind: 'success', title: 'License renewed', body: 'Term extended by one year.' });
      await load();
    } catch (e) {
      toast({ kind: 'error', title: 'Renew failed', body: e instanceof Error ? e.message : 'Could not renew.' });
    } finally { setBusy(false); }
  };
  // A failed POST is NOT caught here: ReasonDialog shows the thrown message in place and stays
  // open with the typed reason (36E 1.3.4), the same as LicenseDetail and the bulk bar. (`load`
  // reports its own failures, so a failed re-read after a successful revoke never throws.)
  const doRevoke = async (reason: string) => {
    if (!revokeId) return;
    setBusy(true);
    try {
      await apiRequest(`/licenses/${revokeId}/revoke`, { method: 'POST', body: JSON.stringify({ reason }) });
      toast({ kind: 'success', title: 'License revoked', body: 'Seats freed back to the pool.' });
      setRevokeId(null);
      await load();
    } finally { setBusy(false); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading account…</div>;
  if (error || !org) return <div style={{ padding: 24 }}><AlertBanner kind="error">{error || 'Account not found'}</AlertBanner></div>;

  // org_kind is the discriminator: a tenant is an end-customer, never a reseller.
  const isTenant = org.org_kind === 'tenant';
  const isReseller = !isTenant && (resold?.customer_kind || org.customer_kind) === 'reseller';
  const econ = resold?.economics;
  const assignedSeats = (resold?.downstream || []).reduce((s, d) => s + (d.seats || 0), 0);

  return (
    <div>
      <PageHeader
        breadcrumb={['Accounts', org.name]}
        back onBack={() => navigate(`${base}/accounts`)}
        onCrumb={(i) => i === 0 && navigate(`${base}/accounts`)}
        title={org.name}
        subtitle={`${isTenant ? 'Tenant' : isReseller ? 'Reseller partner' : 'Direct customer'} · ${isTenant ? `${allocation?.seats ?? 0} seats` : `${org.license_count ?? 0} license${(org.license_count ?? 0) !== 1 ? 's' : ''}`}`}
        actions={canMutate ? <><Btn icon="mail" disabled title="Account contact is not yet supported.">Contact</Btn><Btn icon="refresh-cw" disabled title="Account-wide renewal is not yet supported; use the per-license action below.">Renew</Btn><Btn kind="primary" accent={SALES} icon="arrow-up-circle" onClick={() => navigate('/sales/quotes/new')}>Propose Upsell</Btn></> : undefined}
      />

      {org.org_kind === 'tenant' && org.parent_id && (
        <div style={{ marginBottom: 14 }}>
          <AlertBanner kind="info">
            This is a downstream tenant running under a platform license.{' '}
            <a href={`${base}/accounts/${org.parent_id}`} onClick={(e) => { e.preventDefault(); navigate(`${base}/accounts/${org.parent_id}`); }} style={{ color: 'var(--accent)', fontWeight: 600 }}>View platform account →</a>
          </AlertBanner>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 16 }}>
        {isTenant ? (
          <>
            <Kpi label="MRR (resold)" value={allocation?.mrr ? moneyK(allocation.mrr) : '$0'} accent={SALES} icon="dollar-sign" />
            <Kpi label="Seats (used / allocated)" value={`${allocation?.used ?? 0} / ${allocation?.seats ?? 0}`} accent={SALES} icon="users" />
            <Kpi label="Billing" value={allocation?.billing_state || '—'} accent={SALES} icon="receipt" />
            <Kpi label="Relationship" value="Tenant" accent={SALES} icon="git-branch" />
          </>
        ) : (
          <>
            <Kpi label="MRR" value={org.mrr ? moneyK(org.mrr) : '$0'} accent={SALES} icon="dollar-sign" />
            <Kpi label="Active Licenses" value={org.active_licenses ?? 0} accent={SALES} icon="key-round" />
            <Kpi label="Total Licenses" value={org.license_count ?? 0} accent={SALES} icon="key" />
            <Kpi label="Relationship" value={isReseller ? 'Reseller' : 'Direct'} accent={SALES} icon={isReseller ? 'git-branch' : 'building-2'} />
          </>
        )}
      </div>

      {isTenant ? (
        <Card style={{ overflow: 'hidden' }}>
          <CardHead
            title={`Allocation under ${allocation?.owner?.name || 'platform owner'}`}
            sub="This tenant runs under a platform license — its seats, usage & billing"
            right={allocation?.platform_license ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn sm icon="building-2" onClick={() => allocation.owner && navigate(`${base}/accounts/${allocation.owner.id}`)}>Platform account</Btn>
                <Btn sm icon="key" onClick={() => allocation.platform_license && navigate(`${base}/licenses/${allocation.platform_license.id}`, { state: { from: { type: 'account', id: org.id, name: org.name } } })}>Platform license</Btn>
              </div>
            ) : undefined}
          />
          {!allocation ? (
            <div style={empty}>No active allocation for this tenant.</div>
          ) : (
            <div style={{ padding: '16px 20px', display: 'grid', gap: 16 }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 6 }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Vendor seats</span>
                  <span style={{ fontWeight: 600 }}>{allocation.used} / {allocation.seats}</span>
                </div>
                <UtilBar used={allocation.used} total={allocation.seats} />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center' }}>
                <div><div style={kpiLabel}>Billing</div><Pill bg="var(--ps-pill-blue-bg)" fg="var(--ps-pill-blue-tx)">{allocation.billing_state}</Pill></div>
                <div><div style={kpiLabel}>Platform license</div><MaskedKey value={allocation.platform_license?.license_key} size={12} /></div>
                {allocation.ciic_limit != null && (
                  <div><div style={kpiLabel}>CIIC customers</div><span style={{ fontSize: 13, fontWeight: 600 }}>{allocation.ciic_used ?? 0} / {allocation.ciic_limit}</span></div>
                )}
                <div>
                  <div style={kpiLabel}>Modules</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {Object.entries(allocation.modules || {}).filter(([, v]) => v).map(([k]) => <Pill key={k} bg="var(--accent-light)" fg="var(--accent)">{k.toUpperCase()}</Pill>)}
                    {Object.values(allocation.modules || {}).filter(Boolean).length === 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>}
                  </div>
                </div>
              </div>
              {allocation.escalated_license_id && (
                <AlertBanner kind="info">This tenant has been escalated to its own license.</AlertBanner>
              )}
            </div>
          )}
        </Card>
      ) : isReseller ? (
        <Card style={{ overflow: 'hidden' }}>
          <CardHead title={`Resold customers — ${org.name}`} sub="Downstream customers this partner provisions from its platform pool"
            right={canMutate ? <div style={{ display: 'flex', gap: 8 }}><Btn sm icon="building-2" disabled title="Customer onboarding is not yet supported.">Onboard customer</Btn><Btn sm kind="primary" accent={SALES} icon="plus" disabled title="Pool allocation is not yet supported.">Allocate from pool</Btn></div> : undefined} />
          {econ && (
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)', display: 'flex', gap: 24, justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{assignedSeats.toLocaleString()} seats resold to {resold?.downstream.length || 0} customer{(resold?.downstream.length || 0) !== 1 ? 's' : ''}</span>
              <div style={{ display: 'flex', gap: 18 }}>
                <Econ label="Wholesale (we bill)" value={moneyK(econ.wholesale_mrr)} />
                <Econ label="Resold (partner bills)" value={moneyK(econ.resold_mrr)} />
                <Econ label="Partner margin" value={econ.margin_pct + '%'} color={SALES} />
              </div>
            </div>
          )}
          {(resold?.downstream.length || 0) === 0 ? (
            <div style={empty}>No downstream customers allocated yet.</div>
          ) : (
            <Table cols={[{ label: 'Downstream customer' }, { label: 'Seats', align: 'right' }, { label: 'Used', align: 'right' }, { label: 'MRR', align: 'right' }, { label: 'Billing' }, { label: 'Status' }, { label: 'Since' }]}>
              {resold!.downstream.map((c, i) => (
                <Row key={c.organization_id || i}>
                  <Td style={{ fontWeight: 600 }}><CompanyChip id={c.organization_id} name={c.name} origin={{ type: 'account', id: org.id, name: org.name }} icon={false} /></Td>
                  <Td align="right"><Mono>{c.seats}</Mono></Td>
                  <Td align="right"><Mono>{c.used}</Mono></Td>
                  <Td align="right"><Mono>{c.mrr ? moneyK(c.mrr) : '—'}</Mono></Td>
                  <Td><Pill bg="var(--ps-pill-blue-bg)" fg="var(--ps-pill-blue-tx)">{c.billing_state}</Pill></Td>
                  <Td><StatusBadge status={c.status} /></Td>
                  <Td style={{ color: 'var(--text-secondary)' }}>{c.since ? new Date(c.since).toLocaleDateString() : '—'}</Td>
                </Row>
              ))}
            </Table>
          )}
        </Card>
      ) : (
        <Card style={{ overflow: 'hidden' }}>
          <CardHead title={`Licensing — ${org.name}`} sub="Every license this customer holds · assign, renew & revoke here"
            right={canMutate ? <div style={{ display: 'flex', gap: 8 }}><Btn sm icon="gift" onClick={() => navigate(`${base}/console`)}>Issue Trial</Btn><Btn sm kind="primary" accent={SALES} icon="plus" onClick={() => navigate('/sales/licenses/new')}>Assign License</Btn></div> : undefined} />
          {licenses.length === 0 ? (
            <div style={empty}>No licenses yet.{canMutate && <> Use <strong>Assign License</strong> or <strong>Issue Trial</strong> to onboard.</>}</div>
          ) : (
            <Table cols={[{ label: 'License' }, { label: 'Tier' }, { label: 'Scope' }, { label: 'Seats', align: 'right' }, { label: 'Status' }, { label: 'Expires' }, { label: '', align: 'right' }]}>
              {licenses.map((l) => (
                <Row key={l.id} onClick={() => navigate(`${base}/licenses/${l.id}`, { state: { from: { type: 'account', id: org.id, name: org.name } } })}>
                  <Td><MaskedKey value={l.license_id} size={12} /></Td>
                  <Td><TierPill tier={l.tier} /></Td>
                  <Td><Pill bg={l.scope === 'platform' ? 'var(--accent-light)' : 'var(--bg-muted)'} fg={l.scope === 'platform' ? 'var(--accent)' : 'var(--text-secondary)'}>{l.scope || 'org'}</Pill></Td>
                  <Td align="right"><Mono>{l.scope === 'platform' ? (l.included_org_seats ?? 0) : (l.vendor_limit ?? 0)}</Mono></Td>
                  <Td><StatusBadge status={l.status} /></Td>
                  <Td style={{ color: 'var(--text-secondary)' }}>{l.expires_at ? new Date(l.expires_at).toLocaleDateString() : '—'}</Td>
                  <Td align="right" onClick={(e) => e.stopPropagation()}>
                    {canMutate && (
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <Btn sm icon="refresh-cw" disabled={busy || l.status !== 'active'} onClick={() => renew(l.id)}>Renew</Btn>
                        <Btn sm icon="ban" disabled={busy || l.status === 'revoked'} onClick={() => setRevokeId(l.id)} style={{ color: 'var(--ps-pill-red-tx)', borderColor: 'var(--ps-pill-red-tx)', background: 'var(--ps-pill-red-bg)' }}>Revoke</Btn>
                      </div>
                    )}
                  </Td>
                </Row>
              ))}
            </Table>
          )}
        </Card>
      )}

      {/* Commercial trial — the per-company trial clock + vendor cap on this
          tenant's allocation, with adjust/start/convert (admin-only) and the
          append-only trial history. Only tenants run under an allocation. */}
      {isTenant && allocation && (
        <TrialPanel allocation={allocation} canMutate={canMutate} accent={SALES} onChanged={load} />
      )}

      {/* Members — the people with access to this account. Previously only the
          legacy /organizations/:id page rendered a roster, so accounts reached
          through the persona workspaces showed no members at all. Mutating
          controls are gated behind canMutate (Audit persona is read-only). */}
      <OrgMembersCard orgId={org.id} canMutate={canMutate} accent={SALES} />

      {/* Installs (bidirectional Sales↔Ops) — the Fleet installs tied to this
          company; click through to the Fleet view keeping the workspace. */}
      <Card style={{ overflow: 'hidden', marginTop: 16 }}>
        <CardHead title="Installs" sub="Deployments tied to this company · jump to Fleet for live telemetry & ops"
          right={installs.length > 0 ? <Btn sm icon="server" onClick={() => navigate(`${base}/fleet`)}>Open Fleet</Btn> : undefined} />
        {installs.length === 0 ? (
          <div style={empty}>No installs reporting for this company yet.</div>
        ) : (
          <Table cols={[{ label: 'Install' }, { label: 'Version' }, { label: 'Health' }, { label: 'Last seen' }, { label: '', align: 'right' }]}>
            {installs.map((ins, i) => (
              <Row key={ins.instance_id || i} onClick={() => navigate(`${base}/fleet`, { state: { from: { type: 'account', id: org.id, name: org.name } } })}>
                <Td style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}><Icon name="server" size={14} color="var(--text-muted)" />{ins.hostname || ins.instance_id || '—'}</Td>
                <Td><Mono style={{ fontSize: 12 }}>{ins.product_version || '—'}</Mono></Td>
                <Td>{ins.health_status ? <StatusBadge status={ins.health_status} /> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</Td>
                <Td style={{ color: 'var(--text-secondary)' }}>{ins.last_seen_at ? formatRelativeTime(ins.last_seen_at) : '—'}</Td>
                <Td align="right"><Icon name="chevron-right" size={15} color="var(--text-muted)" /></Td>
              </Row>
            ))}
          </Table>
        )}
      </Card>

      <EntitlementsCard kind={isReseller ? 'platform' : 'direct'} />

      {/* Revoke is irreversible: a reason AND a typed confirm, the same as LicenseDetail and the
          bulk bar (D-LICSAFE-4). The phrase is the last four characters of THIS licence's key, so
          it cannot be satisfied by habit and cannot belong to a neighbouring row. */}
      <ReasonDialog
        open={revokeTarget !== null}
        title="Revoke license?"
        body="The license stops validating on the next check and its seats free back to the pool. This cannot be undone. The reason is logged to the audit trail."
        confirmLabel="Revoke"
        danger
        maxLength={500}
        confirmPhrase={revokeTarget ? (revokeTarget.license_id || revokeTarget.id).slice(-4) : undefined}
        onConfirm={doRevoke}
        onCancel={() => setRevokeId(null)}
      />
    </div>
  );
}

const empty: React.CSSProperties = { padding: '28px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 };
const kpiLabel: React.CSSProperties = { fontSize: 10.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 4 };

export default SalesAccount;
